import { httpResource } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { ProfileStore } from '@rpgc-ai/store/profile/profile.store';

import { ESubscriptionPlan } from '@rpgc-ai/shared-interfaces/interfaces/profile.interface';

export enum EChatWaitTime {
  LESS_THAN_MINUTE = 'less_than_minute',
  ABOUT_MINUTE = 'about_minute',
  ONE_TWO_MINUTES = 'one_two_minutes',
  SEVERAL_MINUTES = 'several_minutes',
}

export const ChatWaitTimeTranslations: Record<EChatWaitTime, string> = {
  [EChatWaitTime.LESS_THAN_MINUTE]: 'MODALS.CHAT_QUERY.LESS_THAN_MINUTE',
  [EChatWaitTime.ABOUT_MINUTE]: 'MODALS.CHAT_QUERY.ABOUT_MINUTE',
  [EChatWaitTime.ONE_TWO_MINUTES]: 'MODALS.CHAT_QUERY.ONE_TWO_MINUTES',
  [EChatWaitTime.SEVERAL_MINUTES]: 'MODALS.CHAT_QUERY.SEVERAL_MINUTES',
};

export interface IQueueStreamData {
  /** Задержка в миллисекундах (0 = без задержки) */
  delayMs: number;
  queuePosition: number;
  /** i18n-ключ для отображения: 'less_than_minute' | 'about_minute' | 'one_two_minutes' | 'several_minutes' */
  estimatedWaitText: string;
}

@Injectable({ providedIn: 'root' })
export class QueueService {
  readonly #profileStore = inject(ProfileStore);

  readonly #resource = httpResource<IQueueStreamData>(() => {
    const canCheck = this.#canCheck();
    if (!canCheck) {
      return;
    }

    return {
      url: `chats/queue-check`,
      method: 'GET',
    };
  });

  // Variables
  readonly #canCheck = signal(false);
  readonly isInQueue = signal(false);
  readonly queueData = signal<IQueueStreamData | null>(null);

  // Computed variables
  readonly isPaidUser = computed<boolean>(() => this.#profileStore.subscription() !== ESubscriptionPlan.NONE);

  constructor() {
    this.#updateStatusEffect();
  }

  checkPlaceInQueue(): void {
    // Платные юзеры — сразу null
    if (this.isPaidUser()) {
      this.clearQueue();
      return;
    }
    this.#canCheck.set(true);
  }

  clearQueue(): void {
    this.queueData.set(null);
    this.isInQueue.set(false);
  }

  #updateStatusEffect(): void {
    effect(() => {
      const value = this.#resource.value();
      untracked(() => {
        this.#canCheck.set(false);
        if (!value) {
          return;
        }
        this.queueData.set(value);
        this.isInQueue.set(!!value && value.delayMs > 0);
      });
    });
  }
}
